import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLive } from "./live";
import { showNotification } from "./notify";
import { useRunning } from "./running";
import { base64ToBlob, localFiles, tempChats } from "./temp";
import type { Chat, ChatDetail, Message, Part } from "./types";
import { onEvent, startEvents, stopEvents, type ServerEvent } from "./ws";

function upsertChat(qc: QueryClient, chat: Chat) {
  qc.setQueriesData<Chat[]>({ queryKey: ["chats", "list"] }, (list) => {
    if (!list) return list;
    const rest = list.filter((c) => c.id !== chat.id);
    // A space's quick-box requests stay out of the lists until continued as a chat.
    if (chat.archived || chat.settings?.quick) return rest;
    return [chat, ...rest].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.updated_at.localeCompare(a.updated_at));
  });
  qc.setQueryData<ChatDetail>(["chat", chat.id], (d) => (d ? { ...d, chat } : d));
}

function upsertMessage(qc: QueryClient, m: Message) {
  qc.setQueryData<ChatDetail>(["chat", m.chat_id], (d) => {
    if (!d) return d;
    const idx = d.messages.findIndex((x) => x.id === m.id);
    const messages = idx >= 0 ? d.messages.map((x) => (x.id === m.id ? m : x)) : [...d.messages, m];
    return { ...d, messages };
  });
}

function handle(qc: QueryClient, ev: ServerEvent) {
  switch (ev.type) {
    case "chat.created":
    case "chat.updated":
      upsertChat(qc, ev.chat);
      if (ev.type === "chat.created") qc.invalidateQueries({ queryKey: ["chats", "list"] });
      if (ev.chat.folder_id) qc.invalidateQueries({ queryKey: ["chats", "space", ev.chat.folder_id] });
      return;
    case "chat.deleted":
      qc.setQueriesData<Chat[]>({ queryKey: ["chats", "list"] }, (list) => list?.filter((c) => c.id !== ev.chat_id));
      qc.removeQueries({ queryKey: ["chat", ev.chat_id] });
      return;
    case "message.created":
      useLive.getState().seed(ev.message);
      upsertMessage(qc, ev.message);
      return;
    case "notification.created":
      qc.invalidateQueries({ queryKey: ["notifications"] });
      return;
    case "task_run.updated":
      qc.invalidateQueries({ queryKey: ["tasks"] });
      return;
    case "space.changed":
      qc.invalidateQueries({ queryKey: ["space", ev.space_id] });
      return;
    case "worker.status":
      qc.invalidateQueries({ queryKey: ["workers"] });
      qc.invalidateQueries({ queryKey: ["mcp", "available"] });
      return;
    case "agent.status":
      qc.invalidateQueries({ queryKey: ["agent-sessions"] });
      return;
    case "reconnected":
      void useRunning.getState().load();
      useLive.setState((s) => ({ live: Object.fromEntries(Object.entries(s.live).filter(([, l]) => l.status !== "streaming")) }));
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["chat"] });
      qc.invalidateQueries({ queryKey: ["space"] });
      return;
    case "unauthorized":
      qc.setQueryData(["me"], null);
      return;
    case "gen.file": {
      // A file a tool made in a private temporary chat: it is kept here, not on the server.
      const f = ev.file as { id: string; name: string; mime: string; data: string };
      void localFiles.put({ id: f.id, name: f.name, mime: f.mime, blob: base64ToBlob(f.data, f.mime) });
      return;
    }
  }
  if (ev.type.startsWith("gen.")) {
    useRunning.getState().apply(ev);
    const ok = useLive.getState().apply(ev);
    if (ev.type === "gen.done" && (ev.status === "done" || ev.status === "error")) void notifyDone(qc, ev);
    if (ev.chat_id && (!ok || ev.type === "gen.done")) {
      if (ev.type === "gen.done") {
        qc.setQueryData<ChatDetail>(["chat", ev.chat_id], (d) =>
          d
            ? {
                ...d,
                messages: d.messages.map((m) =>
                  m.id === ev.message_id ? { ...m, parts: ev.parts, status: ev.status, error: ev.error, usage: ev.usage } : m,
                ),
              }
            : d,
        );
      } else {
        qc.invalidateQueries({ queryKey: ["chat", ev.chat_id] });
      }
    }
  }
}

function findChatTitle(qc: QueryClient, chatId: string): string | null {
  const detail = qc.getQueryData<ChatDetail>(["chat", chatId]);
  if (detail) return detail.chat.title;
  for (const [, list] of qc.getQueriesData<Chat[]>({ queryKey: ["chats", "list"] })) {
    const c = list?.find((x) => x.id === chatId);
    if (c) return c.title;
  }
  return null;
}

/** A system notification for a finished reply (only when switched on and the tab is hidden). */
async function notifyDone(qc: QueryClient, ev: ServerEvent) {
  let title: string | null;
  let url: string;
  if (ev.chat_id) {
    // Task runs are chats too, but they notify through the task's own channels: skip chats we do not list.
    title = findChatTitle(qc, ev.chat_id);
    url = `/c/${ev.chat_id}`;
  } else if (ev.temp_id) {
    // Events reach all of the user's devices, but a temporary chat lives in the browser that started it:
    // one from the phone or the desktop companion is not ours to announce.
    if (!(await tempChats.get(ev.temp_id).catch(() => null))) return;
    title = "Temporary chat";
    url = `/temp/${ev.temp_id}`;
  } else return;
  if (title === null) return;
  const text = (ev.parts as Part[])
    .filter((p) => p.type === "text")
    .map((p) => (p as { text: string }).text)
    .join("")
    .replace(/<think>[\s\S]*?(<\/think>|$)/g, "")
    .replace(/[#*_`>]/g, "")
    .trim();
  void showNotification(ev.status === "error" ? `Reply failed: ${title}` : title, ev.status === "error" ? ev.error || "Something went wrong" : text || "Reply finished", url, `reply-${ev.message_id}`);
}

/** Connects the event socket while a user is logged in and routes events into the query cache. */
export function useServerEvents(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    startEvents();
    const off = onEvent((ev) => handle(qc, ev));
    void useRunning.getState().load();
    return () => {
      off();
      stopEvents();
    };
  }, [enabled, qc]);
}
